/**
 * Вкладка «Инструкция» «Голосового стенда» (`?tab=prompt`): что агент отдаёт модели при старте разговора с
 * настройками формы — текст инструкции (`voicelab/call_instruction.md` с подставленной карточкой, частыми
 * ответами и темами) и инструменты режима: что о них видит модель и что на самом деле делает код.
 * `GET /api/voice-lab/instruction`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { type LabInstruction, MODES, labInstructionQuery } from "@/lib/voiceLab";

type Props = {
  query: Parameters<typeof labInstructionQuery>[0] | null;
  label: string;
  setMode: (mode: string) => void;
};

const pill = (on: boolean) =>
  `rounded-full border px-2.5 py-0.5 text-[12px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;

/** Линии сценариев: голос — звонки, чат — переписка бота @urtoperatorbot; у каждой свои версии. */
const КАНАЛЫ = [
  ["voice", "Голос"],
  ["chat", "Чат"],
] as const;
/** Тип обращения голосовой линии: обзвон заявки или входящий звонок (у чата — только обзвон). */
const ТИПЫ = [
  ["obzvon", "Обзвон заявки"],
  ["vhod", "Входящий звонок"],
] as const;

/** Инструменты, которые модель получает отдельным списком функций: что видит она и что делает код. */
export function ToolsTable({ d }: { d: Pick<LabInstruction, "tools" | "other"> }) {
  return (
    <div className="flex flex-col gap-2" data-annot="инструменты агента">
      <h2 className="text-[14px] font-semibold">Инструменты · {d.tools.length}</h2>
      <div className="text-fg-muted">
        Модель сама решает, когда вызвать функцию; вызов выполняет наш агент на сервере и возвращает модели текст.
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border text-left text-fg-muted">
            <th className="py-1.5 pr-3 font-normal">Функция</th>
            <th className="py-1.5 pr-3 font-normal">Что видит модель</th>
            <th className="py-1.5 font-normal">Что делает код</th>
          </tr>
        </thead>
        <tbody>
          {d.tools.map((t) => (
            <tr key={t.name} className="border-b border-border/60 align-top">
              <td className="py-1.5 pr-3">
                <div className="font-mono text-[12px]">{t.name}</div>
                <div className="text-[11.5px] text-fg-muted">{t.args}</div>
              </td>
              <td className="py-1.5 pr-3">{t.модели}</td>
              <td className="py-1.5">{t.код}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.other.length > 0 && (
        <div className="text-fg-muted">
          В этом режиме нет: <span className="font-mono text-[12px]">{d.other.join(", ")}</span>
        </div>
      )}
    </div>
  );
}

export function VoiceLabPrompt({ query, label, setMode }: Props) {
  const [канал, setКанал] = useState("voice");
  const [тип, setТип] = useState("obzvon");
  const q = useQuery({
    ...labInstructionQuery(
      query ? { ...query, channel: канал, kind: канал === "voice" ? тип : "obzvon" } : {
        model: "",
        mode: "",
        location_id: 0,
        name: "",
        interest: "",
        request_date: "",
      },
    ),
    enabled: query != null,
  });
  const d = q.data;
  return (
    <section
      className="flex max-w-4xl flex-col gap-4 text-[12.5px]"
      data-annot="инструкция агента"
    >
      <div className="flex flex-col gap-1.5">
        <div className="text-fg-muted">
          Для настроек вкладки «Разговор»: {label}
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-annot="линия сценария">
          {КАНАЛЫ.map(([код, имя]) => (
            <button key={код} type="button" className={pill(канал === код)} onClick={() => setКанал(код)}>
              {имя}
            </button>
          ))}
          {канал === "voice" && (
            <>
              <span className="mx-1 h-4 w-px bg-border" aria-hidden />
              {ТИПЫ.map(([код, имя]) => (
                <button key={код} type="button" className={pill(тип === код)} onClick={() => setТип(код)}>
                  {имя}
                </button>
              ))}
            </>
          )}
          {d?.label && <span className="font-mono text-[11.5px] text-fg-muted">{d.label}</span>}
        </div>
        {канал === "voice" && тип === "vhod" && (
          <div className="text-[11.5px] text-fg-muted">
            Входящий — как в бою: имени, салона и интереса нет, город робот узнаёт у клиентки.
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(MODES).map(([код, имя]) => (
            <button
              key={код}
              type="button"
              className={pill(query?.mode === код)}
              onClick={() => setMode(код)}
            >
              {имя}
            </button>
          ))}
        </div>
      </div>
      {q.isLoading && <div className="text-fg-muted">Собираю инструкцию…</div>}
      {q.error && <div className="text-err">{String(q.error)}</div>}
      {d && (
        <>
          <ToolsTable d={d} />
          <div className="flex flex-col gap-2" data-annot="текст инструкции">
            <h2 className="text-[14px] font-semibold">
              Инструкция · {d.text.length.toLocaleString("ru")} знаков
            </h2>
            <pre className="whitespace-pre-wrap break-words rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-3 font-sans text-[12.5px] leading-relaxed">
              {d.text}
            </pre>
          </div>
        </>
      )}
    </section>
  );
}
